/** Workflow reaction id: wf:{category}:{slug} — one per category per message. */
/** Free reaction id: free:{slug} — multiple allowed, user toggles own only. */

import { normalizeEmoji } from './emojiUtils';

const wf = (category, slug, emoji, label, tone = 'slate') => ({
  id: `wf:${category}:${slug}`,
  category,
  slug,
  emoji,
  label,
  tone,
  type: 'workflow',
});

const free = (slug, emoji, label) => ({
  id: `free:${slug}`,
  slug,
  emoji,
  label,
  type: 'free',
});

/** Selecting this from chat creates a Task — it is not a workflow type. */
export const WORK_ITEM_TYPE_TASK_ID = 'wf:work-item-type:task';

export const isWorkItemTypeTaskReaction = (reactionId) =>
  reactionId === WORK_ITEM_TYPE_TASK_ID;

/** True for work-item types that create/lock a workflow (everything except Task). */
export const isWorkflowCreatingWorkItemType = (reactionId) =>
  typeof reactionId === 'string'
  && reactionId.startsWith('wf:work-item-type:')
  && reactionId !== WORK_ITEM_TYPE_TASK_ID;

export const WORKFLOW_REACTION_CATEGORIES = [
  {
    id: 'work-item-type',
    label: 'Work Item Type',
    hint: 'Task creates a task · other types create a workflow (locked after set)',
    reactions: [
      wf('work-item-type', 'epic', '🟣', 'Epic', 'violet'),
      wf('work-item-type', 'feature', '📘', 'Feature', 'blue'),
      wf('work-item-type', 'story', '📖', 'Story', 'blue'),
      wf('work-item-type', 'bug', '🐞', 'Bug', 'red'),
      wf('work-item-type', 'defect', '🪲', 'Defect', 'red'),
      wf('work-item-type', 'improvement', '⚡', 'Improvement', 'amber'),
      wf('work-item-type', 'task', '🛠', 'Task', 'slate'),
      wf('work-item-type', 'spike', '🔬', 'Spike', 'cyan'),
      wf('work-item-type', 'tech-debt', '🔧', 'Technical Debt', 'slate'),
      wf('work-item-type', 'enhancement', '💡', 'Enhancement', 'amber'),
      wf('work-item-type', 'requirement', '📋', 'Requirement', 'indigo'),
      wf('work-item-type', 'leads', '🧲', 'Leads', 'green'),
    ],
  },
  {
    id: 'workflow-status',
    label: 'Workflow Status',
    hint: 'Only one per message',
    reactions: [
      wf('workflow-status', 'new', '🆕', 'New', 'slate'),
      wf('workflow-status', 'acknowledged', '👀', 'Acknowledged', 'blue'),
      wf('workflow-status', 'accepted', '📥', 'Accepted', 'blue'),
      wf('workflow-status', 'assigned', '👨‍💻', 'Assigned', 'indigo'),
      wf('workflow-status', 'started', '🚀', 'Started', 'cyan'),
      wf('workflow-status', 'working', '🛠', 'Working', 'cyan'),
      wf('workflow-status', 'blocked', '⏸', 'Blocked', 'red'),
      wf('workflow-status', 'waiting', '⏳', 'Waiting', 'amber'),
      wf('workflow-status', 'investigating', '🔍', 'Investigating', 'amber'),
      wf('workflow-status', 'ready-for-test', '🧪', 'Ready for Test', 'teal'),
      wf('workflow-status', 'testing', '✔️', 'Testing', 'teal'),
      wf('workflow-status', 'retest-required', '🔁', 'Retest Required', 'amber'),
      wf('workflow-status', 'verified', '✅', 'Verified', 'green'),
      wf('workflow-status', 'ready-for-release', '📦', 'Ready for Release', 'green'),
      wf('workflow-status', 'deployed', '🚀', 'Deployed', 'green'),
      wf('workflow-status', 'closed', '🎉', 'Closed', 'green'),
      wf('workflow-status', 'rejected', '❌', 'Rejected', 'red'),
      wf('workflow-status', 'reopened', '♻️', 'Reopened', 'amber'),
      wf('workflow-status', 'future-fix', '🗓️', 'Future-Fix', 'slate'),
    ],
  },
  {
    id: 'priority',
    label: 'Priority',
    hint: 'Only one per message',
    reactions: [
      wf('priority', 'critical', '🔥', 'Critical', 'red'),
      wf('priority', 'high', '🔴', 'High', 'red'),
      wf('priority', 'medium', '🟠', 'Medium', 'amber'),
      wf('priority', 'low', '🟡', 'Low', 'amber'),
      wf('priority', 'nice-to-have', '⚪', 'Nice to Have', 'slate'),
    ],
  },
  {
    id: 'urgency',
    label: 'Urgency',
    hint: 'Sets a deadline countdown for the assignee',
    reactions: [
      wf('urgency', 'urgent', '🚨', 'Urgent', 'red'),
    ],
  },
  {
    id: 'severity',
    label: 'Severity',
    hint: 'Only one per message',
    reactions: [
      wf('severity', 'blocker', '💥', 'Blocker', 'red'),
      wf('severity', 'critical', '🔥', 'Critical', 'red'),
      wf('severity', 'major', '⚠️', 'Major', 'amber'),
      wf('severity', 'minor', '📌', 'Minor', 'blue'),
      wf('severity', 'trivial', 'ℹ️', 'Trivial', 'slate'),
    ],
  },
  {
    id: 'agile-stage',
    label: 'Agile Stage',
    hint: 'Only one per message',
    reactions: [
      wf('agile-stage', 'backlog', '📋', 'Backlog', 'slate'),
      wf('agile-stage', 'grooming', '🤔', 'Grooming', 'blue'),
      wf('agile-stage', 'sprint-ready', '🎯', 'Sprint Ready', 'indigo'),
      wf('agile-stage', 'in-sprint', '🚀', 'In Sprint', 'cyan'),
      wf('agile-stage', 'sprint-complete', '🏁', 'Sprint Complete', 'green'),
      wf('agile-stage', 'released', '📦', 'Released', 'green'),
    ],
  },
  {
    id: 'code-review',
    label: 'Code Review',
    hint: 'Only one per message',
    reactions: [
      wf('code-review', 'awaiting-review', '⏳', 'Awaiting Review', 'amber'),
      wf('code-review', 'reviewing', '👀', 'Reviewing', 'blue'),
      wf('code-review', 'lgtm', '✅', 'LGTM', 'green'),
      wf('code-review', 'changes-requested', '🔄', 'Changes Requested', 'amber'),
      wf('code-review', 'approved', '📦', 'Approved', 'green'),
      wf('code-review', 'merged', '🔀', 'Merged', 'green'),
    ],
  },
  {
    id: 'testing',
    label: 'Testing',
    hint: 'Only one per message',
    reactions: [
      wf('testing', 'test-cases-pending', '📝', 'Test Cases Pending', 'slate'),
      wf('testing', 'test-cases-ready', '🧪', 'Test Cases Ready', 'teal'),
      wf('testing', 'testing-started', '▶️', 'Testing Started', 'cyan'),
      wf('testing', 'passed', '✔️', 'Passed', 'green'),
      wf('testing', 'failed', '❌', 'Failed', 'red'),
      wf('testing', 'retest', '🔁', 'Retest', 'amber'),
      wf('testing', 'automated', '🤖', 'Automated', 'indigo'),
      wf('testing', 'regression-passed', '📊', 'Regression Passed', 'green'),
      wf('testing', 'smoke-passed', '🚀', 'Smoke Passed', 'green'),
    ],
  },
  {
    id: 'build-deployment',
    label: 'Build / Deployment',
    hint: 'Only one per message',
    reactions: [
      wf('build-deployment', 'build-running', '🏗', 'Build Running', 'slate'),
      wf('build-deployment', 'build-passed', '✅', 'Build Passed', 'green'),
      wf('build-deployment', 'build-failed', '❌', 'Build Failed', 'red'),
      wf('build-deployment', 'deploying', '🚀', 'Deploying', 'cyan'),
      wf('build-deployment', 'production', '🌍', 'Production', 'indigo'),
      wf('build-deployment', 'rolled-back', '⏪', 'Rolled Back', 'amber'),
    ],
  },
  {
    id: 'environment',
    label: 'Environment',
    hint: 'Only one per message',
    reactions: [
      wf('environment', 'local', '💻', 'Local', 'slate'),
      wf('environment', 'dev', '🧪', 'Dev', 'cyan'),
      wf('environment', 'qa', '🔷', 'QA', 'blue'),
      wf('environment', 'uat', '📦', 'UAT', 'indigo'),
      wf('environment', 'production', '🌍', 'Production', 'green'),
    ],
  },
  {
    id: 'risk',
    label: 'Risk',
    hint: 'Only one per message',
    reactions: [
      wf('risk', 'no-risk', '🟢', 'No Risk', 'green'),
      wf('risk', 'low-risk', '🟡', 'Low Risk', 'amber'),
      wf('risk', 'medium-risk', '🟠', 'Medium Risk', 'amber'),
      wf('risk', 'high-risk', '🔴', 'High Risk', 'red'),
      wf('risk', 'blocking', '☠️', 'Blocking', 'red'),
    ],
  },
  {
    id: 'customer-impact',
    label: 'Customer Impact',
    hint: 'Only one per message',
    reactions: [
      wf('customer-impact', 'no-impact', '😊', 'No Impact', 'green'),
      wf('customer-impact', 'minor-impact', '⚠️', 'Minor Impact', 'amber'),
      wf('customer-impact', 'major-impact', '🔥', 'Major Impact', 'red'),
      wf('customer-impact', 'production-issue', '🚨', 'Production Issue', 'red'),
    ],
  },
  {
    id: 'ownership',
    label: 'Ownership',
    hint: 'Only one per message',
    reactions: [
      wf('ownership', 'developer', '👨‍💻', 'Developer', 'indigo'),
      wf('ownership', 'qa', '🧪', 'QA', 'teal'),
      wf('ownership', 'ui', '🎨', 'UI', 'violet'),
      wf('ownership', 'devops', '🏗', 'DevOps', 'slate'),
      wf('ownership', 'product-owner', '👔', 'Product Owner', 'blue'),
      wf('ownership', 'business-analyst', '📊', 'Business Analyst', 'cyan'),
    ],
  },
  {
    id: 'approval',
    label: 'Approval',
    hint: 'Only one per message',
    reactions: [
      wf('approval', 'pending', '⏳', 'Pending approval', 'amber'),
      wf('approval', 'approved', '👍', 'Approved', 'green'),
      wf('approval', 'rejected', '👎', 'Rejected', 'red'),
    ],
  },
  {
    id: 'documentation',
    label: 'Documentation',
    hint: 'Only one per message',
    reactions: [
      wf('documentation', 'required', '📝', 'Required', 'amber'),
      wf('documentation', 'in-progress', '✍️', 'In Progress', 'blue'),
      wf('documentation', 'completed', '✅', 'Completed', 'green'),
    ],
  },
];

export const FREE_REACTIONS = [
  free('like', '👍', 'Like'),
  free('love', '❤️', 'Love'),
  free('great-job', '👏', 'Great Job'),
  free('thanks', '🙌', 'Thanks'),
  free('celebrate', '🎉', 'Celebrate'),
  free('agree', '💯', 'Agree'),
  free('seen', '👀', 'Seen'),
  free('thinking', '🤔', 'Thinking'),
  free('question', '❓', 'Question'),
  free('idea', '💡', 'Idea'),
  free('fyi', '📢', 'FYI'),
  free('urgent', '🚨', 'Urgent'),
];

export const FREE_REACTION_BY_EMOJI = Object.fromEntries(
  FREE_REACTIONS.map((reaction) => [reaction.emoji, reaction]),
);

export const mergeWorkflowCategories = (customItems = []) => {
  const extrasByCategory = {};
  const extraById = {};
  (customItems || []).forEach((item) => {
    const category = item?.category;
    const id = item?.reactionId || item?.id;
    if (!category || !id) return;
    const reaction = {
      id,
      category,
      slug: item.slug || String(id).split(':').pop(),
      emoji: item.emoji || '•',
      label: item.label || item.slug || 'Custom',
      tone: item.tone || 'slate',
      type: 'workflow',
      custom: true,
    };
    extraById[id] = reaction;
    if (!extrasByCategory[category]) extrasByCategory[category] = [];
    extrasByCategory[category].push(reaction);
  });
  return {
    categories: WORKFLOW_REACTION_CATEGORIES.map((category) => ({
      ...category,
      reactions: [...category.reactions, ...(extrasByCategory[category.id] || [])],
    })),
    extraById,
  };
};

let orgWorkflowExtraById = {};
let orgWorkflowCustomItems = [];

export const setOrgWorkflowExtras = (items = []) => {
  const merged = mergeWorkflowCategories(items);
  orgWorkflowCustomItems = Array.isArray(items) ? items : [];
  orgWorkflowExtraById = merged.extraById;
};

export const getOrgWorkflowCustomItems = () => orgWorkflowCustomItems;

export const getMergedWorkflowCategories = (customItems) =>
  mergeWorkflowCategories(customItems !== undefined ? customItems : orgWorkflowCustomItems).categories;

export const getMergedWorkflowReactions = (customItems) =>
  getMergedWorkflowCategories(customItems).flatMap((category) => category.reactions);

export const getWorkItemTypeOptions = (customItems, { includeTask = false } = {}) => {
  const category = getMergedWorkflowCategories(customItems).find((c) => c.id === 'work-item-type');
  return (category?.reactions || []).filter((reaction) => (
    includeTask || reaction.id !== WORK_ITEM_TYPE_TASK_ID
  ));
};

export const ALL_WORKFLOW_REACTIONS = WORKFLOW_REACTION_CATEGORIES.flatMap((c) => c.reactions);
export const REACTION_BY_ID = Object.fromEntries([
  ...ALL_WORKFLOW_REACTIONS.map((r) => [r.id, r]),
  ...FREE_REACTIONS.map((r) => [r.id, r]),
]);

export const isWorkflowReaction = (reactionId) =>
  typeof reactionId === 'string' && reactionId.startsWith('wf:');

export const isFreeReaction = (reactionId) =>
  typeof reactionId === 'string' && (reactionId.startsWith('free:') || !isWorkflowReaction(reactionId));

/** Store Free smiles as `free:{slug}` so 👍 Like is never treated as workflow Approved. */
export const canonicalReactionId = (reactionId) => {
  if (!reactionId || typeof reactionId !== 'string') return '';
  const trimmed = reactionId.trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('wf:') || trimmed.startsWith('free:')) return trimmed;
  const normalized = normalizeEmoji(trimmed);
  if (!normalized) return '';
  return FREE_REACTION_BY_EMOJI[normalized]?.id || normalized;
};

export const getWorkflowCategory = (reactionId) => {
  if (!isWorkflowReaction(reactionId)) return null;
  const parts = reactionId.split(':');
  return parts.length >= 2 ? parts[1] : null;
};

export const getWorkflowCategoryPrefix = (categoryId) => `wf:${categoryId}:`;

/** Three-character abbreviation for compact reaction badges (e.g. "Sprint Started" → "SSP"). */
export const getReactionAbbrev = (label) => {
  if (!label) return '···';
  const words = label.trim().split(/\s+/).filter(Boolean);
  const letters = words.join('').replace(/[^a-zA-Z0-9]/g, '');
  if (!letters) return '···';
  if (words.length === 1) {
    return letters.slice(0, 3).toUpperCase().padEnd(3, '·');
  }
  const initials = words.map((w) => w[0]).join('').toUpperCase();
  if (initials.length >= 3) return initials.slice(0, 3);
  return (initials + letters).slice(0, 3).toUpperCase();
};

export const getReactionDisplay = (reactionId) => {
  const known = REACTION_BY_ID[reactionId] || orgWorkflowExtraById[reactionId];
  if (known) {
    return {
      type: known.type,
      emoji: known.emoji,
      label: known.label,
      abbrev: getReactionAbbrev(known.label),
      tone: known.tone || null,
      category: known.category || null,
    };
  }
  if (isWorkflowReaction(reactionId)) {
    const slug = reactionId.split(':').pop() || reactionId;
    const label = slug.replace(/-/g, ' ');
    return {
      type: 'workflow',
      emoji: '•',
      label,
      abbrev: getReactionAbbrev(label),
      tone: 'slate',
      category: getWorkflowCategory(reactionId),
    };
  }
  if (typeof reactionId === 'string' && reactionId.startsWith('free:')) {
    const slug = reactionId.slice('free:'.length);
    const label = slug.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    return {
      type: 'free',
      emoji: '•',
      label,
      abbrev: getReactionAbbrev(label),
      tone: null,
      category: null,
    };
  }
  const legacyFree = FREE_REACTION_BY_EMOJI[reactionId];
  if (legacyFree) {
    return {
      type: 'free',
      emoji: legacyFree.emoji,
      label: legacyFree.label,
      abbrev: getReactionAbbrev(legacyFree.label),
      tone: null,
      category: null,
    };
  }
  return {
    type: 'free',
    emoji: reactionId,
    label: null,
    abbrev: getReactionAbbrev(reactionId),
    tone: null,
    category: null,
  };
};

/** Unicode emoji for notification copy; never a message sentence or raw `free:like` id. */
export const getReactionGlyph = (reactionId) => {
  if (!reactionId) return '';
  const value = String(reactionId).trim();
  if (!value) return '';
  const known = REACTION_BY_ID[value];
  if (known?.emoji) return known.emoji;
  const byLabel = FREE_REACTIONS.find(
    (reaction) => String(reaction.label).toLowerCase() === value.toLowerCase()
  );
  if (byLabel?.emoji) return byLabel.emoji;
  const byEmoji = FREE_REACTION_BY_EMOJI[value];
  if (byEmoji?.emoji) return byEmoji.emoji;
  if (value.includes(':') || /\s/.test(value) || value.length > 8) return '';
  try {
    if (/\p{Extended_Pictographic}/u.test(value)) return value;
  } catch {
    /* older runtimes without unicode property escapes */
  }
  return '';
};

export const splitReactionToMessage = (reactionId, messagePreview, fallbackBody = '') => {
  const fallback = String(fallbackBody || '').trim();
  const toIndex = fallback.lastIndexOf(' to ');
  const fromFallback = toIndex > 0 ? fallback.slice(0, toIndex).trim() : '';
  const glyph = getReactionGlyph(reactionId) || getReactionGlyph(fromFallback) || '👍';
  const msg = String(messagePreview || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    || (toIndex >= 0 ? fallback.slice(toIndex + 4).trim() : '')
    || 'msg';
  return {
    glyph,
    preview: msg,
    line: `${glyph} to ${msg}`,
  };
};

export const formatReactionToMessageLine = (reactionId, messagePreview, fallbackBody = '') =>
  splitReactionToMessage(reactionId, messagePreview, fallbackBody).line;

/**
 * Reactions must be an array. Call sites sometimes pass `{}` as a fallback, and
 * Mongo/API payloads can occasionally be a map — both crash `.forEach` / `.some`.
 */
export const toReactionList = (reactions) => {
  if (Array.isArray(reactions)) return reactions;
  if (!reactions) return [];
  if (typeof reactions === 'object') {
    return Object.values(reactions).filter((item) => item && typeof item === 'object');
  }
  return [];
};

export const partitionMessageReactions = (reactions = []) => {
  const workflow = [];
  const freeList = [];
  toReactionList(reactions).forEach((reaction) => {
    if (isWorkflowReaction(reaction?.emoji)) {
      workflow.push(reaction);
    } else {
      freeList.push(reaction);
    }
  });
  return { workflow, free: freeList };
};

export const WORKFLOW_CLOSED_STATUS_ID = 'wf:workflow-status:closed';
export const WORKFLOW_URGENT_ID = 'wf:urgency:urgent';
export const FREE_URGENT_ID = 'free:urgent';

/** Map free urgent emoji reaction to the workflow urgency tag. */
export const resolveUrgentWorkflowReactionId = (reactionId) => {
  if (reactionId === FREE_URGENT_ID || reactionId === '🚨') return WORKFLOW_URGENT_ID;
  return reactionId;
};

export const messageHasUrgentWorkflow = (reactions = []) =>
  toReactionList(reactions).some((reaction) => reaction?.emoji === WORKFLOW_URGENT_ID);

/** True when the message has at least one workflow reaction (wf:*). */
export const messageHasWorkflowReactions = (reactions = []) =>
  toReactionList(reactions).some((reaction) => reaction?.emoji && isWorkflowReaction(reaction.emoji));

export const getWorkItemTypeId = (reactions = []) =>
  getActiveWorkflowByCategory(reactions)['work-item-type'] || null;

/** Raised workflow: a non-task work-item type. Status alone is not a workflow. */
export const messageIsRaisedWorkflow = (reactions = []) => {
  const typeId = getWorkItemTypeId(reactions);
  return Boolean(typeId && typeId !== WORK_ITEM_TYPE_TASK_ID);
};

export const messageIsTaskWorkItem = (reactions = []) =>
  getWorkItemTypeId(reactions) === WORK_ITEM_TYPE_TASK_ID;

/**
 * Visual state for messages with workflow reactions.
 * Open (not closed) → light yellow + work-item watermark; closed → light green + same watermark.
 * Urgent (open) → red background; blink when deadline is near or overdue.
 */
export const getMessageWorkflowVisual = (reactions = [], workflowByCategory) => {
  const workflowIds = mergeReactionsWithWorkflowCategory(reactions, workflowByCategory)
    .map((reaction) => reaction?.emoji)
    .filter((id) => id && isWorkflowReaction(id));

  if (workflowIds.length === 0) return null;

  const isClosed = workflowIds.includes(WORKFLOW_CLOSED_STATUS_ID);
  const isUrgent = workflowIds.includes(WORKFLOW_URGENT_ID);
  const workItemTypeId = workflowIds.find((id) => id.startsWith('wf:work-item-type:'));
  const typeDisplay = workItemTypeId ? getReactionDisplay(workItemTypeId) : null;
  const isTask = workItemTypeId === WORK_ITEM_TYPE_TASK_ID;
  const isRaised = Boolean(workItemTypeId && !isTask);
  if (!isRaised && !isTask) return null;

  return {
    isClosed,
    isUrgent,
    watermarkLabel: typeDisplay?.label || (isRaised ? 'Workflow' : 'Task'),
    typeLabel: typeDisplay?.label || null,
  };
};

export const getActiveWorkflowByCategory = (reactions = []) => {
  const map = {};
  toReactionList(reactions).forEach((reaction) => {
    if (!isWorkflowReaction(reaction?.emoji)) return;
    const category = getWorkflowCategory(reaction.emoji);
    if (category) map[category] = reaction.emoji;
  });
  return map;
};

/** Keep Closed (and other category tags) on the bubble even if only workflowByCategory was updated. */
export const mergeReactionsWithWorkflowCategory = (reactions, workflowByCategory) => {
  const list = [...toReactionList(reactions)];
  const active = getActiveWorkflowByCategory(list);
  Object.entries(workflowByCategory || {}).forEach(([category, emoji]) => {
    if (!emoji || !category) return;
    if (active[category] === emoji) return;
    if (!active[category]) {
      list.push({ emoji, userIds: [] });
      active[category] = emoji;
      return;
    }
    if (emoji === WORKFLOW_CLOSED_STATUS_ID) {
      const prefix = `wf:${category}:`;
      for (let i = list.length - 1; i >= 0; i -= 1) {
        if (String(list[i]?.emoji || '').startsWith(prefix)) {
          list.splice(i, 1);
        }
      }
      list.push({ emoji, userIds: [] });
      active[category] = emoji;
    }
  });
  return list;
};

const normalizeSearch = (value) => (value || '').trim().toLowerCase();

export const searchReactions = (query, options = {}) => {
  const q = normalizeSearch(query);
  if (!q) return { workflow: [], free: [] };
  const hideCategories = new Set(options.hideCategories || []);
  const categories = options.categories || getMergedWorkflowCategories();
  const workflowPool = categories.flatMap((category) => category.reactions || []);

  const workflow = workflowPool.filter((reaction) => {
    if (hideCategories.has(reaction.category)) return false;
    const category = categories.find((c) => c.id === reaction.category);
    const haystack = [reaction.label, reaction.id, reaction.emoji, category?.label].join(' ').toLowerCase();
    return haystack.includes(q);
  });

  const freeResults = FREE_REACTIONS.filter((reaction) => {
    const haystack = [reaction.label, reaction.id, reaction.emoji].join(' ').toLowerCase();
    return haystack.includes(q);
  });

  return { workflow, free: freeResults };
};

export const getCategoryById = (categoryId) =>
  WORKFLOW_REACTION_CATEGORIES.find((category) => category.id === categoryId);

/** Apply a reaction toggle locally for optimistic UI updates. */
export const applyOptimisticReaction = (reactions = [], reactionId, userId) => {
  if (!reactionId || !userId) return reactions || [];

  const list = [...(reactions || [])];

  if (isWorkflowReaction(reactionId)) {
    const category = getWorkflowCategory(reactionId);
    if (!category) return list;
    const prefix = getWorkflowCategoryPrefix(category);
    const existingSame = list.find((r) => r.emoji === reactionId);
    if (existingSame) {
      return list.filter((r) => r.emoji !== reactionId);
    }
    return [
      ...list.filter((r) => !r.emoji?.startsWith(prefix)),
      { emoji: reactionId, userIds: [userId] },
    ];
  }

  const existingIdx = list.findIndex((r) => r.emoji === reactionId);
  if (existingIdx === -1) {
    return [...list, { emoji: reactionId, userIds: [userId] }];
  }

  const existing = list[existingIdx];
  const userIds = [...(existing.userIds || [])];
  const userIndex = userIds.findIndex((id) => String(id) === String(userId));
  if (userIndex >= 0) {
    userIds.splice(userIndex, 1);
    if (userIds.length === 0) {
      list.splice(existingIdx, 1);
      return list;
    }
    list[existingIdx] = { ...existing, userIds };
    return list;
  }

  list[existingIdx] = { ...existing, userIds: [...userIds, userId] };
  return list;
};

const baseTone = (ownBorder, ownBg, ownText, otherBorder, otherBg, otherText, pickerBorder, pickerBg, pickerText, pickerHover) => ({
  own: `${ownBorder} ${ownBg} ${ownText}`,
  other: `${otherBorder} ${otherBg} ${otherText}`,
  picker: `${pickerBorder} ${pickerBg} ${pickerText} ${pickerHover}`,
});

export const REACTION_TONE_STYLES = {
  red: baseTone(
    'border-red-300/70', 'bg-red-500/20', 'text-red-50',
    'border-red-300', 'bg-red-50', 'text-red-700 dark:border-red-700 dark:bg-red-900/35 dark:text-red-200',
    'border-red-200', 'bg-red-50', 'text-red-700', 'hover:bg-red-100 dark:border-red-800 dark:bg-red-900/30 dark:text-red-200 dark:hover:bg-red-900/50',
  ),
  rose: baseTone(
    'border-rose-300/70', 'bg-rose-500/20', 'text-rose-50',
    'border-rose-300', 'bg-rose-50', 'text-rose-700 dark:border-rose-700 dark:bg-rose-900/35 dark:text-rose-200',
    'border-rose-200', 'bg-rose-50', 'text-rose-700', 'hover:bg-rose-100 dark:border-rose-800 dark:bg-rose-900/30 dark:text-rose-200 dark:hover:bg-rose-900/50',
  ),
  violet: baseTone(
    'border-violet-300/70', 'bg-violet-500/20', 'text-violet-50',
    'border-violet-300', 'bg-violet-50', 'text-violet-700 dark:border-violet-700 dark:bg-violet-900/35 dark:text-violet-200',
    'border-violet-200', 'bg-violet-50', 'text-violet-700', 'hover:bg-violet-100 dark:border-violet-800 dark:bg-violet-900/30 dark:text-violet-200 dark:hover:bg-violet-900/50',
  ),
  blue: baseTone(
    'border-blue-300/70', 'bg-blue-500/20', 'text-blue-50',
    'border-blue-300', 'bg-blue-50', 'text-blue-700 dark:border-blue-700 dark:bg-blue-900/35 dark:text-blue-200',
    'border-blue-200', 'bg-blue-50', 'text-blue-700', 'hover:bg-blue-100 dark:border-blue-800 dark:bg-blue-900/30 dark:text-blue-200 dark:hover:bg-blue-900/50',
  ),
  indigo: baseTone(
    'border-indigo-300/70', 'bg-indigo-500/20', 'text-indigo-50',
    'border-indigo-300', 'bg-indigo-50', 'text-indigo-700 dark:border-indigo-700 dark:bg-indigo-900/35 dark:text-indigo-200',
    'border-indigo-200', 'bg-indigo-50', 'text-indigo-700', 'hover:bg-indigo-100 dark:border-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-200 dark:hover:bg-indigo-900/50',
  ),
  cyan: baseTone(
    'border-cyan-300/70', 'bg-cyan-500/20', 'text-cyan-50',
    'border-cyan-300', 'bg-cyan-50', 'text-cyan-800 dark:border-cyan-700 dark:bg-cyan-900/35 dark:text-cyan-200',
    'border-cyan-200', 'bg-cyan-50', 'text-cyan-800', 'hover:bg-cyan-100 dark:border-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-200 dark:hover:bg-cyan-900/50',
  ),
  teal: baseTone(
    'border-teal-300/70', 'bg-teal-500/20', 'text-teal-50',
    'border-teal-300', 'bg-teal-50', 'text-teal-800 dark:border-teal-700 dark:bg-teal-900/35 dark:text-teal-200',
    'border-teal-200', 'bg-teal-50', 'text-teal-800', 'hover:bg-teal-100 dark:border-teal-800 dark:bg-teal-900/30 dark:text-teal-200 dark:hover:bg-teal-900/50',
  ),
  green: baseTone(
    'border-green-300/70', 'bg-green-500/20', 'text-green-50',
    'border-green-300', 'bg-green-50', 'text-green-700 dark:border-green-700 dark:bg-green-900/35 dark:text-green-200',
    'border-green-200', 'bg-green-50', 'text-green-700', 'hover:bg-green-100 dark:border-green-800 dark:bg-green-900/30 dark:text-green-200 dark:hover:bg-green-900/50',
  ),
  amber: baseTone(
    'border-amber-300/70', 'bg-amber-500/20', 'text-amber-50',
    'border-amber-300', 'bg-amber-50', 'text-amber-800 dark:border-amber-700 dark:bg-amber-900/35 dark:text-amber-200',
    'border-amber-200', 'bg-amber-50', 'text-amber-800', 'hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-200 dark:hover:bg-amber-900/50',
  ),
  slate: baseTone(
    'border-slate-300/70', 'bg-slate-500/20', 'text-slate-50',
    'border-slate-300', 'bg-slate-50', 'text-slate-700 dark:border-slate-600 dark:bg-slate-800/50 dark:text-slate-200',
    'border-slate-200', 'bg-slate-50', 'text-slate-700', 'hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800/40 dark:text-slate-200 dark:hover:bg-slate-800/60',
  ),
};

/** @deprecated */
export const DEV_TONE_STYLES = REACTION_TONE_STYLES;
